import { isStripDisabled } from './stripState'

const CLIP_ENVELOPE_ELIGIBLE_TYPES = new Set([
  'audio',
  'video',
])

export const CLIP_ENVELOPE_VOLUME_PROP = 'volume'
export const CLIP_ENVELOPE_OPACITY_PROP = 'opacity'
export const CLIP_ENVELOPE_OFF_PROP = 'off'

export const CLIP_ENVELOPE_DB_MAX = 12
export const CLIP_ENVELOPE_DB_MIN = -60

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0))
}

function normalizeType(asset) {
  return `${asset?.type || asset?.asset?.type || ''}`.trim().toLowerCase()
}

export function supportsClipEnvelopeAsset(asset) {
  return CLIP_ENVELOPE_ELIGIBLE_TYPES.has(normalizeType(asset))
}

export function getClipEnvelopeCandidates(asset) {
  if (!supportsClipEnvelopeAsset(asset)) return []
  const type = normalizeType(asset)
  if (type === 'video') {
    return [
      CLIP_ENVELOPE_VOLUME_PROP,
      CLIP_ENVELOPE_OPACITY_PROP,
      CLIP_ENVELOPE_OFF_PROP,
    ]
  }
  return [
    CLIP_ENVELOPE_VOLUME_PROP,
    CLIP_ENVELOPE_OFF_PROP,
  ]
}

export function getClipEnvelopeLabel(prop) {
  if (prop === CLIP_ENVELOPE_VOLUME_PROP) return 'Volume'
  if (prop === CLIP_ENVELOPE_OPACITY_PROP) return 'Opacity'
  if (prop === CLIP_ENVELOPE_OFF_PROP) return 'Off'
  return String(prop || '')
}

export function buildDefaultClipEnvelope(prop) {
  if (prop === CLIP_ENVELOPE_VOLUME_PROP) {
    return {
      levelDb: 0,
      fadeIn: 0,
      fadeOut: 0,
    }
  }
  if (prop === CLIP_ENVELOPE_OPACITY_PROP) {
    return {
      level: 1,
      fadeIn: 0,
      fadeOut: 0,
    }
  }
  return null
}

export function clampClipEnvelope(prop, envelope, stripLength = Infinity) {
  const length = Number.isFinite(Number(stripLength)) ? Math.max(0, Number(stripLength)) : Infinity

  if (prop === CLIP_ENVELOPE_VOLUME_PROP) {
    const fadeIn = clamp(envelope?.fadeIn, 0, length)
    let fadeOut = clamp(envelope?.fadeOut, 0, length)
    if (Number.isFinite(length) && fadeIn + fadeOut > length) {
      fadeOut = Math.max(0, length - fadeIn)
    }
    return {
      levelDb: clamp(envelope?.levelDb, CLIP_ENVELOPE_DB_MIN, CLIP_ENVELOPE_DB_MAX),
      fadeIn,
      fadeOut,
    }
  }

  if (prop === CLIP_ENVELOPE_OPACITY_PROP) {
    const fadeIn = clamp(envelope?.fadeIn, 0, length)
    let fadeOut = clamp(envelope?.fadeOut, 0, length)
    if (Number.isFinite(length) && fadeIn + fadeOut > length) {
      fadeOut = Math.max(0, length - fadeIn)
    }
    return {
      level: clamp(envelope?.level, 0, 1),
      fadeIn,
      fadeOut,
    }
  }

  return envelope || null
}

export function getClipEnvelope(strip, prop) {
  const base = buildDefaultClipEnvelope(prop)
  if (!base) return null

  const stored = strip?.clipEnvelopes?.[prop] || null
  return clampClipEnvelope(prop, {
    ...base,
    ...(stored || {}),
  }, strip?.length)
}

export function normalizeClipEnvelopeProp(asset, prop) {
  const candidates = getClipEnvelopeCandidates(asset)
  if (!candidates.length) return null
  if (candidates.includes(prop)) return prop
  if (candidates.includes(CLIP_ENVELOPE_OFF_PROP)) return CLIP_ENVELOPE_OFF_PROP
  return candidates[0] || null
}

export function isClipEnvelopeOffProp(prop) {
  return prop === CLIP_ENVELOPE_OFF_PROP
}

export function dbToGain(db) {
  const safeDb = clamp(db, CLIP_ENVELOPE_DB_MIN, CLIP_ENVELOPE_DB_MAX)
  return Math.pow(10, safeDb / 20)
}

export function getStripBaseVolumeGain(strip) {
  const type = normalizeType(strip)
  let raw = strip?.animation?.values?.volume

  if (!Number.isFinite(Number(raw))) {
    raw = strip?.animation?.keyframes?.[0]?.props?.volume
  }
  if (!Number.isFinite(Number(raw))) {
    raw = strip?.volume
  }

  let gain = Number.isFinite(Number(raw)) ? Number(raw) : 1
  if (type === 'audio' && gain > 1) gain = gain / 100
  return Math.max(0, gain)
}

// Transcript warp Phase 5: per-word volume automation.
// strip.volumeAutomation = [{ start, end, gainDb }] with times in SOURCE
// seconds (matching transcript word timing). Returns a linear multiplier.
//
// Ramp shape: the word itself sits at constant target gain from `start` to
// `end`; smoothstep ramps occupy the ~40ms BEFORE the word (so the onset
// syllable is already at target level) and the ~40ms AFTER it. Ramps live in
// the gaps between words, never inside the word.
export const WORD_GAIN_DB_MAX = 12
export const WORD_GAIN_DB_MIN = -24
const WORD_GAIN_FEATHER_SEC = 0.04

function smoothstep(u) {
  const x = Math.max(0, Math.min(1, u))
  return x * x * (3 - 2 * x)
}

// Pure-data variant used by the audio mixer (no strip object available).
export function evaluateWordAutomationRanges(ranges, sourceTime) {
  if (!Array.isArray(ranges) || !ranges.length) return 1
  const t = Number(sourceTime)
  if (!Number.isFinite(t)) return 1
  const f = WORD_GAIN_FEATHER_SEC
  let mul = 1
  for (const r of ranges) {
    const s = Number(r?.start)
    const e = Number(r?.end)
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue
    if (t < s - f || t > e + f) continue
    const db = clamp(r.gainDb, WORD_GAIN_DB_MIN, WORD_GAIN_DB_MAX)
    const gain = Math.pow(10, db / 20)
    let w = 1
    if (t < s) w = smoothstep((t - (s - f)) / f)
    else if (t > e) w = smoothstep((e + f - t) / f)
    mul *= 1 + (gain - 1) * w
  }
  return mul
}

export function evaluateWordVolumeAutomation(strip, sourceTime) {
  return evaluateWordAutomationRanges(strip?.volumeAutomation, sourceTime)
}

// Snapshot of the data the audio mixer needs to evaluate word gain at exact
// mix time (the strip object itself stays in the store).
export function getStripWordAutomationData(strip) {
  const ranges = strip?.volumeAutomation
  if (!Array.isArray(ranges) || !ranges.length) return null
  const rawScale = Number(strip?.timeScale)
  return {
    ranges,
    sourceStart: Number.isFinite(Number(strip?.sourceStart)) ? Number(strip.sourceStart) : 0,
    timeScale: rawScale > 0 ? rawScale : 1,
  }
}

// Timeline-domain wrapper: maps timeline time through the strip's source
// window (sourceStart/timeScale) before evaluating word automation.
export function resolveStripWordGainMultiplier(strip, timelineTime) {
  const ranges = strip?.volumeAutomation
  if (!Array.isArray(ranges) || !ranges.length) return 1
  const t = Number(timelineTime)
  if (!Number.isFinite(t)) return 1
  const stripStart = Number.isFinite(Number(strip?.start)) ? Number(strip.start) : 0
  const localTime = t - stripStart
  const sourceStart = Number.isFinite(Number(strip?.sourceStart)) ? Number(strip.sourceStart) : 0
  const rawScale = Number(strip?.timeScale)
  const timeScale = rawScale > 0 ? rawScale : 1
  return evaluateWordVolumeAutomation(strip, sourceStart + localTime * timeScale)
}

export function resolveStripEffectiveVolumeGain(strip, { timelineTime = null, lastKnownGain = null } = {}) {
  if (isStripDisabled(strip)) return 0
  // NOTE: Use the bare `Number.isFinite(x)` predicate, NOT `Number.isFinite(Number(x))`.
  // The latter coerces `null` → `0` (a finite number) and silently takes this
  // short-circuit branch with gain 0 whenever a caller doesn't pass a
  // lastKnownGain — the exact bug that produced silent exports because
  // `offlineAudioMix` calls this with only `{ timelineTime }`.
  if (Number.isFinite(lastKnownGain)) {
    return Math.max(0, lastKnownGain)
  }

  const baseGain = getStripBaseVolumeGain(strip)
  if (!Number.isFinite(Number(timelineTime))) return baseGain

  const stripStart = Number.isFinite(Number(strip?.start)) ? Number(strip.start) : 0
  const localTime = Number(timelineTime) - stripStart
  return evaluateVolumeClipEnvelope(strip, localTime, baseGain).gain
}

export function opacityLevelToLineY(level, height) {
  const h = Math.max(1, Number(height) || 1)
  const maxY = Math.max(0, h - 1)
  const safeLevel = clamp(level, 0, 1)
  return maxY - (maxY * safeLevel)
}

export function lineYToOpacityLevel(y, height) {
  const h = Math.max(1, Number(height) || 1)
  const maxY = Math.max(0, h - 1)
  const safeY = clamp(y, 0, maxY)
  if (maxY <= 1e-6) return 1
  return clamp(1 - (safeY / maxY), 0, 1)
}

export function volumeDbToLineY(db, height) {
  const h = Math.max(1, Number(height) || 1)
  const maxY = Math.max(0, h - 1)
  const midY = maxY / 2
  const safeDb = clamp(db, CLIP_ENVELOPE_DB_MIN, CLIP_ENVELOPE_DB_MAX)

  if (safeDb >= 0) {
    const pct = CLIP_ENVELOPE_DB_MAX > 0 ? (safeDb / CLIP_ENVELOPE_DB_MAX) : 0
    return midY - (midY * pct)
  }

  const pct = CLIP_ENVELOPE_DB_MIN < 0 ? (safeDb / CLIP_ENVELOPE_DB_MIN) : 0
  return midY + ((maxY - midY) * pct)
}

export function lineYToVolumeDb(y, height) {
  const h = Math.max(1, Number(height) || 1)
  const maxY = Math.max(0, h - 1)
  const midY = maxY / 2
  const safeY = clamp(y, 0, maxY)

  if (safeY <= midY) {
    const denom = Math.max(1e-6, midY)
    const pct = 1 - (safeY / denom)
    return clamp(pct * CLIP_ENVELOPE_DB_MAX, CLIP_ENVELOPE_DB_MIN, CLIP_ENVELOPE_DB_MAX)
  }

  const denom = Math.max(1e-6, maxY - midY)
  const pct = (safeY - midY) / denom
  return clamp(pct * CLIP_ENVELOPE_DB_MIN, CLIP_ENVELOPE_DB_MIN, CLIP_ENVELOPE_DB_MAX)
}

export function evaluateVolumeClipEnvelope(strip, localTime, baseGain = 1) {
  const activeProp = normalizeClipEnvelopeProp(strip, strip?.clipEnvelopeProp)
  if (activeProp !== CLIP_ENVELOPE_VOLUME_PROP) {
    return {
      applied: false,
      gain: Number.isFinite(Number(baseGain)) ? Number(baseGain) : 1,
      fadeMultiplier: 1,
      targetGain: Number.isFinite(Number(baseGain)) ? Number(baseGain) : 1,
      envelope: null,
    }
  }

  const envelope = getClipEnvelope(strip, CLIP_ENVELOPE_VOLUME_PROP)
  if (!envelope) {
    return {
      applied: false,
      gain: Number.isFinite(Number(baseGain)) ? Number(baseGain) : 1,
      fadeMultiplier: 1,
      targetGain: Number.isFinite(Number(baseGain)) ? Number(baseGain) : 1,
      envelope: null,
    }
  }

  const length = Math.max(0, Number(strip?.length) || 0)
  const t = clamp(localTime, 0, length)
  const safeBaseGain = Math.max(0, Number.isFinite(Number(baseGain)) ? Number(baseGain) : 1)
  const targetGain = safeBaseGain * dbToGain(envelope.levelDb)

  let fadeMultiplier = 1
  if (envelope.fadeIn > 0 && t < envelope.fadeIn) {
    fadeMultiplier = Math.min(fadeMultiplier, t / Math.max(1e-6, envelope.fadeIn))
  }
  if (envelope.fadeOut > 0 && t > Math.max(0, length - envelope.fadeOut)) {
    fadeMultiplier = Math.min(
      fadeMultiplier,
      Math.max(0, length - t) / Math.max(1e-6, envelope.fadeOut)
    )
  }

  return {
    applied: true,
    gain: Math.max(0, targetGain * Math.max(0, Math.min(1, fadeMultiplier))),
    fadeMultiplier: Math.max(0, Math.min(1, fadeMultiplier)),
    targetGain,
    envelope,
  }
}

export function evaluateOpacityClipEnvelope(strip, localTime, baseOpacity = 1) {
  const activeProp = normalizeClipEnvelopeProp(strip, strip?.clipEnvelopeProp)
  if (activeProp !== CLIP_ENVELOPE_OPACITY_PROP) {
    return {
      applied: false,
      opacity: clamp(baseOpacity, 0, 1),
      fadeMultiplier: 1,
      targetOpacity: clamp(baseOpacity, 0, 1),
      envelope: null,
    }
  }

  const envelope = getClipEnvelope(strip, CLIP_ENVELOPE_OPACITY_PROP)
  if (!envelope) {
    return {
      applied: false,
      opacity: clamp(baseOpacity, 0, 1),
      fadeMultiplier: 1,
      targetOpacity: clamp(baseOpacity, 0, 1),
      envelope: null,
    }
  }

  const length = Math.max(0, Number(strip?.length) || 0)
  const t = clamp(localTime, 0, length)
  const targetOpacity = clamp(baseOpacity, 0, 1) * clamp(envelope.level, 0, 1)

  let fadeMultiplier = 1
  if (envelope.fadeIn > 0 && t < envelope.fadeIn) {
    fadeMultiplier = Math.min(fadeMultiplier, t / Math.max(1e-6, envelope.fadeIn))
  }
  if (envelope.fadeOut > 0 && t > Math.max(0, length - envelope.fadeOut)) {
    fadeMultiplier = Math.min(
      fadeMultiplier,
      Math.max(0, length - t) / Math.max(1e-6, envelope.fadeOut)
    )
  }

  return {
    applied: true,
    opacity: clamp(targetOpacity * Math.max(0, Math.min(1, fadeMultiplier)), 0, 1),
    fadeMultiplier: Math.max(0, Math.min(1, fadeMultiplier)),
    targetOpacity,
    envelope,
  }
}
